import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { travelService } from '../../services/travelService';
import AddFlightForm from './AddFlightForm';
import AddTrainForm from './AddTrainForm';
import AddBusForm from './AddBusForm';
import AddCarForm from './AddCarForm';
import './AddTravel.css';

const AddTravel = ({ isOpen, onClose, onSave, initialTravel = null, warningBanner = null }) => {
    const [selectedType, setSelectedType] = useState(null);
    const [formData, setFormData] = useState({ details: {} });
    const [error, setError] = useState('');
    const [travelTypes, setTravelTypes] = useState({});

    // Load travel types dynamically
    useEffect(() => {
        if (isOpen) {
            import('@trip-planner-monorepo/travel/components/constants/travelTypes').then(module => {
                setTravelTypes(module.TRAVEL_TYPES);
            });
        }
    }, [isOpen]);

    // Add effect to handle initialTravel
    useEffect(() => {
        if (isOpen && initialTravel) {
            setSelectedType(initialTravel.type);
            setFormData(initialTravel);
        } else if (isOpen) {
            setSelectedType(null);
            setFormData({ details: {} });
        }
    }, [isOpen, initialTravel]);

    const handleTypeSelect = (type) => {
        setSelectedType(type);
        setFormData({ details: {} });
        setError('');
    };

    const handleClose = () => {
        setSelectedType(null);
        setFormData({ details: {} });
        setError('');
        onClose();
    };

    const handleFlightSubmit = async (flightData) => {
        try {
            console.log('Flight data received:', flightData);

            // If editing existing travel and summary hasn't changed, keep original
            const preserveSummary = initialTravel &&
                initialTravel.summary &&
                (!flightData.summary || flightData.summary === initialTravel.summary);

            // Prepare travel data with flight details
            const travelData = {
                type: 'flight',
                details: {
                    airline: flightData.details.airline,
                    flightNumber: flightData.details.flightNumber,
                    departureCity: flightData.details.departureCity,
                    departureAirport: flightData.details.departureAirport,
                    arrivalCity: flightData.details.arrivalCity,
                    arrivalAirport: flightData.details.arrivalAirport,
                    departureTime: flightData.details.departureTime,
                    arrivalTime: flightData.details.arrivalTime,
                    departureTimezone: flightData.details.departureTimezone,
                    arrivalTimezone: flightData.details.arrivalTimezone,
                    bookingAgent: flightData.details.bookingAgent,
                    bookingReference: flightData.details.bookingReference,
                    ticketNumbers: flightData.details.ticketNumbers || [],
                    passengers: flightData.details.passengers || [],
                    classOfService: flightData.details.classOfService,
                    baggageAllowance: flightData.details.baggageAllowance,
                    notes: flightData.details.notes
                },
                status: flightData.status || 'Planned',
                // Use original summary if editing and summary hasn't been explicitly changed
                summary: preserveSummary ? initialTravel.summary : flightData.summary,
                // Ensure dates are never undefined (use null instead)
                startDate: flightData.startDate || null,
                endDate: flightData.endDate || null,
                cost: flightData.cost || {
                    amount: 0,
                    currency: 'USD',
                    paid: false
                }
            };

            console.log('Prepared travel data:', travelData);
            await onSave(travelData);
            handleClose();
        } catch (error) {
            setError('Failed to save flight details. Please try again.');
            console.error('Error saving flight:', error);
        }
    };

    const handleTrainSubmit = async (trainData) => {
        try {
            console.log('Train data received:', trainData);

            // If editing existing travel and summary hasn't changed, keep original
            const preserveSummary = initialTravel &&
                initialTravel.summary &&
                (!trainData.summary || trainData.summary === initialTravel.summary);

            // Prepare travel data with train details
            const travelData = {
                type: 'train',
                details: {
                    operator: trainData.details.operator,
                    trainNumber: trainData.details.trainNumber,
                    departureCity: trainData.details.departureCity,
                    departureStation: trainData.details.departureStation,
                    arrivalCity: trainData.details.arrivalCity,
                    arrivalStation: trainData.details.arrivalStation,
                    departureTime: trainData.details.departureTime,
                    arrivalTime: trainData.details.arrivalTime,
                    departureTimezone: trainData.details.departureTimezone,
                    arrivalTimezone: trainData.details.arrivalTimezone,
                    platform: trainData.details.platform,
                    coach: trainData.details.coach,
                    seat: trainData.details.seat,
                    classOfService: trainData.details.classOfService,
                    bookingAgent: trainData.details.bookingAgent,
                    bookingReference: trainData.details.bookingReference,
                    ticketNumbers: trainData.details.ticketNumbers || [],
                    passengers: trainData.details.passengers || [],
                    notes: trainData.details.notes
                },
                status: trainData.status || 'Planned',
                // Use original summary if editing and summary hasn't been explicitly changed
                summary: preserveSummary ? initialTravel.summary : trainData.summary,
                // Ensure dates are never undefined (use null instead)
                startDate: trainData.startDate || null,
                endDate: trainData.endDate || null,
                cost: trainData.cost || {
                    amount: 0,
                    currency: 'USD',
                    paid: false
                }
            };

            console.log('Prepared train data:', travelData);
            await onSave(travelData);
            handleClose();
        } catch (error) {
            setError('Failed to save train details. Please try again.');
            console.error('Error saving train:', error);
        }
    };

    const handleBusSubmit = async (busData) => {
        try {
            console.log('Bus data received:', busData);

            // If editing existing travel and summary hasn't changed, keep original
            const preserveSummary = initialTravel &&
                initialTravel.summary &&
                (!busData.summary || busData.summary === initialTravel.summary);

            // Prepare travel data with bus details
            const travelData = {
                type: 'bus',
                details: {
                    operator: busData.details.operator,
                    serviceNumber: busData.details.serviceNumber,
                    departureCity: busData.details.departureCity,
                    departureStop: busData.details.departureStop,
                    arrivalCity: busData.details.arrivalCity,
                    arrivalStop: busData.details.arrivalStop,
                    departureTime: busData.details.departureTime,
                    arrivalTime: busData.details.arrivalTime,
                    departureTimezone: busData.details.departureTimezone,
                    arrivalTimezone: busData.details.arrivalTimezone,
                    platform: busData.details.platform,
                    seat: busData.details.seat,
                    bookingAgent: busData.details.bookingAgent,
                    bookingReference: busData.details.bookingReference,
                    ticketNumbers: busData.details.ticketNumbers || [],
                    passengers: busData.details.passengers || [],
                    notes: busData.details.notes
                },
                status: busData.status || 'Planned',
                // Use original summary if editing and summary hasn't been explicitly changed
                summary: preserveSummary ? initialTravel.summary : busData.summary,
                // Ensure dates are never undefined (use null instead)
                startDate: busData.startDate || null,
                endDate: busData.endDate || null,
                cost: busData.cost || {
                    amount: 0,
                    currency: 'USD',
                    paid: false
                }
            };

            console.log('Prepared bus data:', travelData);
            await onSave(travelData);
            handleClose();
        } catch (error) {
            setError('Failed to save bus details. Please try again.');
            console.error('Error saving bus:', error);
        }
    };

    const handleCarSubmit = async (carData) => {
        try {
            console.log('Car data received:', carData);

            // If editing existing travel and summary hasn't changed, keep original
            const preserveSummary = initialTravel &&
                initialTravel.summary &&
                (!carData.summary || carData.summary === initialTravel.summary);

            // Prepare travel data with car rental details
            const travelData = {
                type: 'car',
                details: {
                    rentalCompany: carData.details.rentalCompany,
                    bookingReference: carData.details.bookingReference,
                    pickupLocation: carData.details.pickupLocation,
                    returnLocation: carData.details.returnLocation,
                    pickupTime: carData.details.pickupTime,
                    returnTime: carData.details.returnTime,
                    vehicleClass: carData.details.vehicleClass,
                    notes: carData.details.notes
                },
                status: carData.status || 'Planned',
                // Use original summary if editing and summary hasn't been explicitly changed
                summary: preserveSummary ? initialTravel.summary : carData.summary,
                cost: carData.cost || {
                    amount: 0,
                    currency: 'USD',
                    paid: false
                }
            };

            console.log('Prepared car data:', travelData);
            await onSave(travelData);
            handleClose();
        } catch (error) {
            setError('Failed to save car rental details. Please try again.');
            console.error('Error saving car:', error);
        }
    };

    const handleInputChange = (fieldName, value) => {
        setFormData(prev => ({
            ...prev,
            details: {
                ...prev.details,
                [fieldName]: value
            }
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!selectedType) return;

        const typeConfig = travelTypes[selectedType];
        const missingFields = Object.entries(typeConfig.fields)
            .filter(([fieldName, field]) =>
                field.required && !formData.details[fieldName])
            .map(([_, field]) => field.label);

        if (missingFields.length > 0) {
            setError(`Please fill in all required fields: ${missingFields.join(', ')}`);
            return;
        }

        const travelData = {
            type: selectedType,
            details: formData.details,
            status: 'Planned'
        };

        onSave(travelData);
        handleClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={handleClose}
            contentLabel="Add Travel"
            className="travel-modal"
            overlayClassName="travel-modal-overlay"
        >
            <div className="travel-modal-content">
                <h2>Add Travel</h2>
                {/* Warning Banner - e.g., for AI-extracted data */}
                {warningBanner}
                {error && <div className="error-message">{error}</div>}

                {!selectedType ? (
                    // Type Selection View
                    <div className="travel-type-grid">
                        {Object.entries(travelTypes).map(([type, config]) => (
                            <button
                                key={type}
                                className="travel-type-button"
                                onClick={() => handleTypeSelect(type)}
                            >
                                <config.icon className="travel-type-icon" />
                                <span>{config.label}</span>
                            </button>
                        ))}
                    </div>
                ) : selectedType === 'flight' ? (
                    // Flight Form - Using enhanced flight form
                    <AddFlightForm
                        onSubmit={handleFlightSubmit}
                        onCancel={handleClose}
                        initialData={initialTravel?.type === 'flight' ? initialTravel.details : {}}
                    />
                ) : selectedType === 'train' ? (
                    // Train Form - Using our new train form
                    <AddTrainForm
                        onSubmit={handleTrainSubmit}
                        onCancel={handleClose}
                        initialData={initialTravel?.type === 'train' ? initialTravel.details : {}}
                    />
                ) : selectedType === 'bus' ? (
                    // Bus Form - Using our new bus form
                    <AddBusForm
                        onSubmit={handleBusSubmit}
                        onCancel={handleClose}
                        initialData={initialTravel?.type === 'bus' ? initialTravel.details : {}}
                    />
                ) : selectedType === 'car' ? (
                    // Car Form - Using dedicated car rental form
                    <AddCarForm
                        onSubmit={handleCarSubmit}
                        onCancel={handleClose}
                        initialData={initialTravel?.type === 'car' ? initialTravel.details : {}}
                    />
                ) : (
                    // Default Form for Other Types
                    <form onSubmit={handleSubmit} className="travel-form">
                        <div className="form-grid">
                            {Object.entries(travelTypes[selectedType].fields).map(([fieldName, field]) => (
                                <div key={fieldName} className="form-field">
                                    <label>
                                        {field.label}
                                        {field.required && <span className="required">*</span>}
                                    </label>
                                    {field.type === 'datetime' ? (
                                        <input
                                            type="datetime-local"
                                            value={formData.details[fieldName] || ''}
                                            onChange={(e) => handleInputChange(fieldName, e.target.value)}
                                            className="form-input"
                                        />
                                    ) : (
                                        <input
                                            type="text"
                                            value={formData.details[fieldName] || ''}
                                            onChange={(e) => handleInputChange(fieldName, e.target.value)}
                                            className="form-input"
                                            placeholder={field.placeholder || ''}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="form-actions">
                            <button type="submit" className="save-button">Save</button>
                            <button type="button" onClick={handleClose} className="cancel-button">Cancel</button>
                        </div>
                    </form>
                )}
            </div>
        </Modal>
    );
};

export default AddTravel;